import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import environment from '_environments'
import { getPostHogClient } from '@/services/posthog'
import pkg from '../../package.json'

/**
 * Sentry setup shared by both app shells.
 *
 * Kept out of the `@/services` barrel for the same reason as `posthog.ts`: the
 * barrel also builds the legacy Cognito/Amplify setup and, through
 * `user.service`, the legacy Redux store. The barrel re-exports this function,
 * so the legacy entry keeps importing it from `./services` (MAM-1015).
 */
type ConfigureSentryOptions = {
  /**
   * The release name events are filed under. Source maps only resolve when it
   * matches the release they were uploaded with, so the new shell passes the
   * name `config/rspack.config.js` uploads its maps under. Legacy passes
   * nothing and keeps its historical name.
   */
  release?: string
  /**
   * Whether Session Replay records `<canvas>` content. Legacy leaves it on.
   * The canvas integration forces `preserveDrawingBuffer: true` on every
   * WebGL context and reads each canvas back with `createImageBitmap` twice a
   * second (@sentry-internal/replay-canvas 10.49.0). The new shell turns it
   * off, because there that canvas is the live MapLibre map, and under
   * software WebGL the readback stalled the page (MAM-1017).
   */
  recordCanvas?: boolean
}

let isSentryInitialized = false

export const configureSentry = ({
  release,
  recordCanvas = true,
}: ConfigureSentryOptions = {}) => {
  if (isSentryInitialized) {
    return
  }

  // Initialize PostHog first for the Sentry integration
  const postHogInstance = getPostHogClient()

  Sentry.init({
    dsn: environment.keys.sentry,
    // v10.4+: user IP is only attached when sendDefaultPii is true
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      ...(recordCanvas ? [Sentry.replayCanvasIntegration()] : []),
      // Only add PostHog integration if PostHog is available and initialized
      ...(postHogInstance && environment.keys.posthog
        ? [
            posthog.sentryIntegration({
              organization: 'myassetmap',
              projectId: 4506713766428672,
              prefix: 'https://myassetmap.sentry.io/organizations/',
              severityAllowList: ['error', 'info'], // optional: here is set to handle captureMessage (info) and captureException (error)
            }),
          ]
        : []),
    ],

    environment: environment.env,
    release: release ?? `myassetmap-frontend@${pkg.version}-${environment.env}`,

    // Errors thrown entirely inside third-party HubSpot scripts (tracking,
    // banner, forms, conversations) are not actionable from our codebase —
    // e.g. Sentry FRONTEND-7N, where HubSpot's URL-param parser throws on
    // malformed inbound query strings. Keep them out of Sentry. (MAM-794)
    denyUrls: [
      /hs-scripts\.com/i,
      /hs-analytics\.net/i,
      /hs-banner\.com/i,
      /hscollectedforms\.net/i,
      /usemessages\.com/i,
      /js\.hubspot\.com/i,
      // HubSpot analytics bundle as it appears in stack frames when the
      // domain is stripped: /analytics/{timestamp}/{portalId}.js
      /\/analytics\/\d+\/245161158\.js/,
    ],

    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/*myassetmap\.com\//],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enabled: environment.build !== 'development',
  })

  isSentryInitialized = true
}
